/**
 * Client-side logging utility (for browser)
 * Handles logging for frontend code
 * Also works in Node.js by falling back to process.env
 *
 * Shared types and formatting are imported from the canonical logger module.
 */

import { formatLogMessage, type LogLevel, type LogContext } from './logger.js'

/** Subset of import.meta.env shape we actually read */
interface ViteMetaEnv {
  DEV?: boolean
  PROD?: boolean
}

class ClientLogger {
  private isDevelopment: boolean

  constructor() {
    // Always use process.env in Node.js (server-side)
    // import.meta.env is only available in Vite/browser builds
    if (typeof process !== 'undefined' && process.env) {
      this.isDevelopment = process.env.NODE_ENV === 'development' || !process.env.NODE_ENV
    } else {
      // Browser environment - safely access import.meta.env
      let metaEnv: ViteMetaEnv | null = null
      try {
        if (typeof import.meta !== 'undefined') {
          metaEnv = import.meta.env as ViteMetaEnv
        }
      } catch {
        // Ignore if import.meta is not available
      }

      // Check localStorage for debug override (useful for testing in production builds)
      let localStorageDebug = false
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorageDebug = localStorage.getItem('debug') === 'true'
        }
      } catch {
        // Ignore localStorage errors (e.g., in private browsing)
      }

      if (metaEnv) {
        this.isDevelopment = metaEnv.DEV === true || localStorageDebug
      } else {
        // Fallback to localStorage check
        this.isDevelopment = localStorageDebug
      }
    }
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    return formatLogMessage(level, message, context)
  }

  debug(_message: string, _context?: LogContext): void {
    // Suppress debug logs to avoid DevTools floods during local runs
    return
  }

  info(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console -- logger method outputs via console by design
      console.info(this.formatMessage('info', message, context))
    }
  }

  warn(message: string, context?: LogContext): void {
    console.warn(this.formatMessage('warn', message, context))
  }

  log(...args: unknown[]): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console -- logger method outputs via console by design
      console.log('[FBC]', ...args)
    }
  }

  error(...args: unknown[]): void {
    console.error('[FBC]', ...args)
  }

  group(label: string): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console -- logger method outputs via console by design
      console.group(label)
    }
  }

  groupEnd(): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console -- logger method outputs via console by design
      console.groupEnd()
    }
  }

  table(data: unknown): void {
    if (this.isDevelopment) {
      // eslint-disable-next-line no-console -- logger method outputs via console by design
      console.table(data)
    }
  }

  logRequest(method: string, url: string, data?: unknown): void {
    if (this.isDevelopment) {
      this.group(`API Request: ${method} ${url}`)
      if (data) this.log('Data:', data)
      this.groupEnd()
    }
  }

  logResponse(method: string, url: string, status: number, data?: unknown): void {
    if (this.isDevelopment) {
      const tag = status >= 200 && status < 300 ? 'OK' : 'ERR'
      this.group(`[${tag}] API Response: ${method} ${url} (${status})`)
      if (data) this.log('Data:', data)
      this.groupEnd()
    }
  }
}

export const logger = new ClientLogger()